import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Operation } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import { useAlertData } from '../data/useMockData';
import { MOCK_DENSITY } from '../data/mock';
import { isZoneMetricFresh, useLiveZoneMetrics, type CurrentZoneMetrics } from '../hooks/useLiveZoneMetrics';
import { useOperations } from '../hooks/useOperations';
import { useZones } from '../hooks/useZones';
import { useOperationAlertSummaries, type OperationAlertSummary } from '../hooks/useOperationAlertSummaries';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
  useCrowdFilterActivity,
} from '../hooks/useCrowdActivityAudit';
import { useCrowdTaxonomy } from '../hooks/useCrowdTaxonomy';
import { useTeams, type CrowdTeam } from '../hooks/useTeams';
import { useVenues } from '../hooks/useVenues';
import { parseLocalDateTimeField } from '../utils/local-date-time-range';
import { operationEntryPathForRole } from '../utils/operation-entry-routing';
import {
  Btn,
  FONT_SIZE,
  FONT_WEIGHT,
  Icons,
  KLabel,
  LETTER_SPACING,
  Panel,
  RADIUS,
  SIZE,
  SPACE,
  type ColorPalette,
} from '@vigilo/design-system';
import { getCrowdC } from '../../design/crowdPalette';

import { alertCountColor, loadColor as zoneLoadColor } from '../utils/zone-status';
type OperationTab = 'active' | 'scheduled' | 'paused' | 'closed' | 'archived';
type OperationStatusFilter = 'all' | OperationTab;

const TABS: Array<{ id: OperationTab; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'paused', label: 'Paused' },
  { id: 'closed', label: 'Closed' },
  { id: 'archived', label: 'Archived' },
];

const OPERATION_REGISTER_COLUMNS = [
  { heading: 'Operation', width: '21%', align: 'left' as const },
  { heading: 'Venue', width: '10%', align: 'left' as const },
  { heading: 'Type', width: '8%', align: 'left' as const },
  { heading: 'Zones', width: '4%', align: 'center' as const },
  { heading: 'Load Index', width: '9%', align: 'center' as const },
  { heading: 'Schedule', width: '14%', align: 'left' as const },
  { heading: 'Teams', width: '12%', align: 'left' as const },
  { heading: 'Status', width: '6%', align: 'center' as const },
  { heading: 'Active Alerts', width: '5%', align: 'center' as const },
  { heading: 'Action', width: '11%', align: 'right' as const },
];

type OperationAlert = ReturnType<typeof useAlertData>['alerts'][number];

interface OperationLoadIndex {
  value: number | null;
  observedZones: number;
  totalZones: number;
  highestZoneLoad: number | null;
  stale: boolean;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function operationZoneCount(op: Operation): number {
  return op.assignedZoneIds.length;
}

function highestZoneCoverageSummary(operations: Operation[]): { label: string; title: string } {
  if (operations.length === 0) return { label: 'No operation zone coverage', title: 'No operation zone coverage' };
  const highestZoneCount = Math.max(...operations.map(operationZoneCount));
  const highestZoneOperations = operations
    .filter(op => operationZoneCount(op) === highestZoneCount)
    .map(op => op.name);
  const zoneLabel = highestZoneCount === 1 ? 'zone' : 'zones';
  const operationNames = highestZoneOperations.join(', ');
  return {
    label: `${highestZoneOperations[0]}: ${highestZoneCount} ${zoneLabel}`,
    title: highestZoneOperations.length > 1 ? operationNames : `${operationNames}: ${highestZoneCount} ${zoneLabel}`,
  };
}

function operationTeamNames(op: Operation, teamById: Map<string, CrowdTeam>): string[] {
  if (op.assignedTeams.length > 0) return op.assignedTeams;
  const defaultTeamName = op.defaultTeamId ? teamById.get(op.defaultTeamId)?.name : null;
  return defaultTeamName ? [defaultTeamName] : [];
}

function tabForOperation(op: Operation): OperationTab {
  if (op.status === 'active') return 'active';
  if (op.status === 'scheduled') return 'scheduled';
  if (op.status === 'paused') return 'paused';
  return 'closed';
}

function operationMatchesTab(op: Operation, tab: OperationTab): boolean {
  if (tab === 'archived') return op.status === 'closed';
  return tabForOperation(op) === tab;
}

function operationStatusLabel(op: Operation, statusFilter: OperationStatusFilter): string {
  if (statusFilter === 'archived') return 'archived';
  return op.status;
}

function statusTone(C: ColorPalette, status: OperationTab): string {
  if (status === 'scheduled') return C.teal;
  if (status === 'active') return C.green;
  if (status === 'paused') return C.amber;
  if (status === 'closed') return C.ember;
  return C.muted;
}

function pillStyle(C: ColorPalette, color: string): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE[5],
    padding: `${SPACE[3]}px ${SPACE[7]}px`,
    borderRadius: RADIUS.sm,
    border: `1px solid ${color}55`,
    background: `${color}18`,
    color,
    fontSize: FONT_SIZE.label,
    fontWeight: FONT_WEIGHT.bold,
    letterSpacing: LETTER_SPACING.wide,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };
}

function isActiveOperationAlert(alert: OperationAlert): boolean {
  return alert.status === 'open' || alert.status === 'acknowledged' || alert.status === 'escalated';
}

function activeAlertCount(
  op: Operation,
  alerts: ReturnType<typeof useAlertData>['alerts'],
  summariesByOperationId?: ReadonlyMap<string, OperationAlertSummary>,
): number {
  const summary = summariesByOperationId?.get(op.id);
  if (summary) return summary.active;
  const zoneIds = new Set(op.assignedZoneIds);
  return alerts.filter(alert => alert.operationId === op.id && zoneIds.has(alert.zoneId) && isActiveOperationAlert(alert)).length;
}

function loadIndexTone(C: ColorPalette, value: number | null): string {
  return value === null ? C.muted : zoneLoadColor(value, C);
}

function loadAvailabilityLabel(op: Operation, loadIndex: OperationLoadIndex): string {
  if (loadIndex.value !== null) {
    return loadIndex.stale ? 'Stale feed' : `${loadIndex.observedZones}/${loadIndex.totalZones} zones`;
  }
  if (op.status === 'scheduled') return 'Scheduled';
  if (op.status === 'paused') return 'Paused';
  if (op.status === 'closed') return 'Closed';
  return 'Awaiting feed';
}

function operationCurrentLoadIndex(op: Operation, rows: CurrentZoneMetrics[], capacityByZoneId: Map<string, number>): OperationLoadIndex {
  const totalZones = op.assignedZoneIds.length;
  if (op.status !== 'active' || totalZones === 0) {
    return { value: null, observedZones: 0, totalZones, highestZoneLoad: null, stale: false };
  }

  const assignedZoneIds = new Set(op.assignedZoneIds);
  const operationRows = rows.filter(row => row.operationId === op.id && assignedZoneIds.has(row.zoneId));
  const loadValues = operationRows
    .map(row => row.capacityUtilisation)
    .filter(value => Number.isFinite(value));

  if (loadValues.length === 0) {
    return { value: null, observedZones: 0, totalZones, highestZoneLoad: null, stale: false };
  }

  // The load index is the share of the event's capacity that is occupied: the same figure the Command Dashboard and the
  // reports show. (The plain average of the areas' percentages would read differently on each screen.)
  let occupied = 0;
  let capacity = 0;
  for (const row of operationRows) {
    if (!Number.isFinite(row.capacityUtilisation)) continue;
    const zoneCapacity = capacityByZoneId.get(row.zoneId)
      ?? (row.capacityUtilisation > 0 ? (row.occupancyCount * 100) / row.capacityUtilisation : 0);
    if (zoneCapacity > 0) {
      occupied += row.occupancyCount;
      capacity += zoneCapacity;
    }
  }

  return {
    value: capacity > 0
      ? Math.round((occupied / capacity) * 100)
      : Math.round(loadValues.reduce((sum, value) => sum + value, 0) / loadValues.length),
    observedZones: loadValues.length,
    totalZones,
    highestZoneLoad: Math.round(Math.max(...loadValues)),
    stale: operationRows.some(row => !isZoneMetricFresh(row)),
  };
}

function mockOperationCurrentLoadIndex(op: Operation): OperationLoadIndex {
  const totalZones = op.assignedZoneIds.length;
  if (op.status !== 'active' || totalZones === 0) {
    return { value: null, observedZones: 0, totalZones, highestZoneLoad: null, stale: false };
  }

  const loadValues = op.assignedZoneIds
    .map(zoneId => MOCK_DENSITY[zoneId]?.capacityUtilisation)
    .filter((value): value is number => Number.isFinite(value));

  if (loadValues.length === 0) {
    return { value: null, observedZones: 0, totalZones, highestZoneLoad: null, stale: false };
  }

  return {
    value: Math.round(loadValues.reduce((sum, value) => sum + value, 0) / loadValues.length),
    observedZones: loadValues.length,
    totalZones,
    highestZoneLoad: Math.round(Math.max(...loadValues)),
    stale: false,
  };
}

function alertBreakdown(
  ops: Operation[],
  alerts: ReturnType<typeof useAlertData>['alerts'],
  summariesByOperationId?: ReadonlyMap<string, OperationAlertSummary>,
) {
  if (summariesByOperationId && ops.every(op => summariesByOperationId.has(op.id))) {
    const summaries = ops.map(op => summariesByOperationId.get(op.id) ?? {
      total: 0,
      active: 0,
      open: 0,
      acknowledged: 0,
      escalated: 0,
      resolved: 0,
    });
    return {
      total: summaries.reduce((sum, summary) => sum + summary.active, 0),
      open: summaries.reduce((sum, summary) => sum + summary.open, 0),
      acknowledged: summaries.reduce((sum, summary) => sum + summary.acknowledged, 0),
      escalated: summaries.reduce((sum, summary) => sum + summary.escalated, 0),
    };
  }
  const operationZoneIdsById = new Map(ops.map(op => [op.id, new Set(op.assignedZoneIds)]));
  const scoped = alerts.filter(alert => {
    const zoneIds = operationZoneIdsById.get(alert.operationId ?? '');
    return zoneIds !== undefined && zoneIds.has(alert.zoneId) && isActiveOperationAlert(alert);
  });
  return {
    total: scoped.length,
    open: scoped.filter(alert => alert.status === 'open').length,
    acknowledged: scoped.filter(alert => alert.status === 'acknowledged').length,
    escalated: scoped.filter(alert => alert.status === 'escalated').length,
  };
}

function operationStartMsForElapsed(op: Operation): number | null {
  const ms = Date.parse(op.actualStart ?? op.scheduledStart);
  return Number.isFinite(ms) ? ms : null;
}

function formatElapsedFromMs(startMs: number | null, nowMs: number): string {
  if (startMs === null) return '--:--:--';
  const seconds = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(value => String(value).padStart(2, '0')).join(':');
}

function useNowTick(enabled: boolean): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [enabled]);
  return nowMs;
}

function rowTextStyle(C: ColorPalette, align: CSSProperties['textAlign'] = 'left'): CSSProperties {
  return {
    display: 'block',
    minWidth: 0,
    color: C.sub,
    fontSize: FONT_SIZE.base,
    lineHeight: 1.35,
    textAlign: align,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
}

function hasInvalidDateRange(fromDate: string, toDate: string): boolean {
  const from = parseLocalDateTimeField(fromDate);
  const to = parseLocalDateTimeField(toDate);
  return from !== null && to !== null && from > to;
}

function overlapsDateRange(op: Operation, fromDate: string, toDate: string): boolean {
  if (op.status === 'active' || op.status === 'paused') return true;

  const from = parseLocalDateTimeField(fromDate);
  const to = parseLocalDateTimeField(toDate);
  if (from === null && to === null) return true;
  if (from !== null && to !== null && from > to) return false;

  const opStart = Date.parse(op.scheduledStart);
  const opEnd = Date.parse(op.scheduledEnd);
  if (!Number.isFinite(opStart) || !Number.isFinite(opEnd)) return false;
  return (from === null || opEnd >= from) && (to === null || opStart <= to);
}

function nextLabel(path: string | null): string {
  if (!path) return '';
  if (path.startsWith('/map')) return 'Map Dashboard';
  if (path.startsWith('/zones')) return 'Zones';
  if (path.startsWith('/alerts')) return 'Alerts';
  if (path.startsWith('/movement')) return 'Movement';
  if (path.startsWith('/flags')) return 'Flagged Tokens';
  if (path.startsWith('/reports')) return 'Reporting';
  if (path.startsWith('/command')) return 'Command Dashboard';
  return 'the requested module';
}

function OperationKpiCard({
  C,
  label,
  value,
  tone,
  icon,
  sub,
  subTitle,
  breakdown,
  sparkline,
}: {
  C: ColorPalette;
  label: string;
  value: number | string;
  tone: string;
  icon: JSX.Element;
  sub: string;
  subTitle?: string;
  breakdown?: Array<{ label: string; value: number; color: string }>;
  sparkline?: boolean;
}) {
  const valueText = String(value);
  const valueSize = valueText.length > 8 ? FONT_SIZE.heading : FONT_SIZE.hero;
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border2}`,
      borderRadius: RADIUS.lg,
      padding: `${SPACE[14]}px ${SPACE[16]}px`,
      minHeight: 118,
      display: 'grid',
      gridTemplateRows: 'auto 1fr auto',
      gap: SPACE[8],
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACE[8] }}>
        <KLabel C={C}>{label}</KLabel>
        <span style={{
          width: 30,
          height: 30,
          borderRadius: RADIUS.md,
          background: `${tone}18`,
          border: `1px solid ${tone}40`,
          color: tone,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>{icon}</span>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: valueSize, fontWeight: FONT_WEIGHT.black, color: tone, lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </div>
        <div title={subTitle ?? sub} style={{ marginTop: SPACE[5], fontSize: FONT_SIZE.xs, color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sub}
        </div>
      </div>
      {sparkline ? (
        <svg viewBox="0 0 200 30" style={{ width: '100%', height: SIZE.kpiSparkline }}>
          <polyline
            points="0,25 28,23 56,18 86,15 118,10 150,8 200,4"
            fill="none"
            stroke={tone}
            strokeWidth="2"
          />
          <polygon points="0,30 0,25 28,23 56,18 86,15 118,10 150,8 200,4 200,30" fill={`${tone}22`} />
        </svg>
      ) : breakdown ? (
        <div style={{ display: 'flex', gap: SPACE[10], paddingTop: SPACE[8], borderTop: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
          {breakdown.map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: SPACE[4] }}>
              <span style={{ width: SIZE.dot, height: SIZE.dot, borderRadius: '50%', background: item.color }} />
              <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>{item.label}</span>
              <span style={{ fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.bold, color: C.text, fontFamily: 'monospace' }}>{item.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

function EmptyState({ C, statusFilter }: { C: ColorPalette; statusFilter: OperationStatusFilter }) {
  const label = statusFilter === 'all' ? 'matching' : statusFilter;
  return (
    <Panel C={C} style={{ padding: SPACE[24], minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: C.muted, marginBottom: SPACE[8] }}><Icons.Calendar size={SIZE.iconXl} /></div>
        <div style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold, color: C.sub }}>
          No {label} operations in the register.
        </div>
      </div>
    </Panel>
  );
}

function OperationRow({
  C,
  op,
  statusFilter,
  venueLabel,
  eventTypeLabel,
  teamById,
  alertCount,
  loadIndex,
  canManage,
  onOpen,
  onActivate,
  onPause,
  onResume,
  onClose,
  onReport,
  onPlayback,
}: {
  C: ColorPalette;
  op: Operation;
  statusFilter: OperationStatusFilter;
  venueLabel: (venueId: string) => string;
  eventTypeLabel: (type: string) => string;
  teamById: Map<string, CrowdTeam>;
  alertCount: number;
  loadIndex: OperationLoadIndex;
  canManage: boolean;
  onOpen: (op: Operation) => void;
  onActivate: (op: Operation) => void;
  onPause: (op: Operation) => void;
  onResume: (op: Operation) => void;
  onClose: (op: Operation) => void;
  onReport: (op: Operation) => void;
  onPlayback: (op: Operation) => void;
}) {
  const status = operationStatusLabel(op, statusFilter);
  const statusTab = status === 'archived' ? 'archived' : tabForOperation(op);
  const rowTone = statusTone(C, statusTab);
  const statusColor = rowTone;
  const teams = operationTeamNames(op, teamById);
  const readOnly = op.status === 'closed' || statusFilter === 'archived';
  const isLive = op.status === 'active' || op.status === 'paused';
  const zonesCount = operationZoneCount(op);
  const zoneLabel = String(zonesCount);
  const zoneTitle = `${zonesCount} ${zonesCount === 1 ? 'zone' : 'zones'} assigned to this operation`;
  const teamsLabel = teams.length > 0 ? teams.join(', ') : 'Unassigned';
  const venue = venueLabel(op.venueId);
  const eventType = eventTypeLabel(op.eventType);
  const loadColor = loadIndexTone(C, loadIndex.value);
  const loadLabel = loadIndex.value === null ? '--' : `${loadIndex.value}%`;
  const loadTitle = loadIndex.value === null
    ? `${loadAvailabilityLabel(op, loadIndex)}: current load index is unavailable for this operation.`
    : `Current load index ${loadIndex.value}% across ${loadIndex.observedZones}/${loadIndex.totalZones} zones. Highest zone ${loadIndex.highestZoneLoad}%.`;
  const nowMs = useNowTick(isLive);
  const elapsedLabel = isLive ? formatElapsedFromMs(operationStartMsForElapsed(op), nowMs) : null;

  return (
    <tr style={{ borderBottom: `1px solid ${C.border}`, background: `${rowTone}08` }}>
      <td style={{ padding: 0, width: 4 }}>
        <div style={{ width: 4, height: 70, background: rowTone }} />
      </td>
      <td style={{ padding: `${SPACE[12]}px ${SPACE[12]}px` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8], minWidth: 0 }}>
          <div style={{
            width: 28,
            height: 28,
            borderRadius: RADIUS.md,
            border: `1px solid ${rowTone}44`,
            background: `${rowTone}14`,
            color: rowTone,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            {readOnly ? <Icons.Lock size={SIZE.iconSm} /> : <Icons.Shield size={SIZE.iconSm} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <div title={op.name} style={{ fontSize: FONT_SIZE.item, fontWeight: FONT_WEIGHT.bold, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{op.name}</div>
            {elapsedLabel && (
              <div style={{ marginTop: SPACE[3], fontSize: FONT_SIZE.micro, color: C.muted, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                Elapsed {elapsedLabel}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={{ padding: `${SPACE[12]}px` }}>
        <span title={venue} style={rowTextStyle(C)}>{venue}</span>
      </td>
      <td style={{ padding: `${SPACE[12]}px` }}>
        <span title={eventType} style={rowTextStyle(C)}>{eventType}</span>
      </td>
      <td style={{ padding: `${SPACE[12]}px`, fontSize: FONT_SIZE.base, color: C.sub, textAlign: 'center' }}>
        <span title={zoneTitle} style={rowTextStyle(C, 'center')}>{zoneLabel}</span>
      </td>
      <td style={{ padding: `${SPACE[10]}px ${SPACE[12]}px`, textAlign: 'center' }}>
        <div title={loadTitle} style={{ display: 'grid', gap: SPACE[5], justifyItems: 'center', minWidth: 0 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: SPACE[3],
            minWidth: 62,
            padding: `${SPACE[5]}px ${SPACE[7]}px`,
            borderRadius: RADIUS.md,
            border: `1px solid ${loadColor}44`,
            background: `${loadColor}14`,
            color: loadColor,
            fontFamily: 'monospace',
            fontSize: FONT_SIZE.item,
            fontWeight: FONT_WEIGHT.black,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}>
            {loadLabel}
          </div>
          <div style={{
            width: 70,
            height: 4,
            borderRadius: 999,
            background: C.faint,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.max(0, Math.min(100, loadIndex.value ?? 0))}%`,
              height: '100%',
              background: loadColor,
              borderRadius: 999,
            }} />
          </div>
          <div style={{
            fontSize: FONT_SIZE.micro,
            color: loadIndex.stale ? C.amber : C.muted,
            fontWeight: FONT_WEIGHT.bold,
            letterSpacing: LETTER_SPACING.field,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}>
            {loadAvailabilityLabel(op, loadIndex)}
          </div>
        </div>
      </td>
      <td style={{ padding: `${SPACE[10]}px ${SPACE[12]}px`, fontSize: FONT_SIZE.base, color: C.sub }}>
        <div style={{ display: 'grid', gap: SPACE[5], minWidth: 0 }}>
          {[
            ['Start', fmtDateTime(op.scheduledStart)],
            ['End', fmtDateTime(op.scheduledEnd)],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'grid', gridTemplateColumns: '42px minmax(0, 1fr)', alignItems: 'baseline', gap: SPACE[8], minWidth: 0 }}>
              <span style={{
                fontSize: FONT_SIZE.micro,
                fontWeight: FONT_WEIGHT.bold,
                letterSpacing: LETTER_SPACING.field,
                textTransform: 'uppercase',
                color: C.muted,
              }}>{label}</span>
              <span title={value} style={{ ...rowTextStyle(C), fontFamily: 'monospace' }}>{value}</span>
            </div>
          ))}
        </div>
      </td>
      <td style={{ padding: `${SPACE[10]}px ${SPACE[12]}px`, fontSize: FONT_SIZE.base, color: C.sub }}>
        <span title={teamsLabel} style={{ ...rowTextStyle(C), color: teams.length > 0 ? C.sub : C.muted }}>{teamsLabel}</span>
      </td>
      <td style={{ padding: `${SPACE[12]}px`, textAlign: 'center' }}>
        <span style={pillStyle(C, statusColor)}>{status}</span>
      </td>
      <td style={{ padding: `${SPACE[12]}px`, textAlign: 'center' }}>
        <span style={{
          fontSize: FONT_SIZE.body,
          fontWeight: FONT_WEIGHT.black,
          color: isLive && alertCount > 0 ? alertCountColor(C) : C.muted,
          fontFamily: 'monospace',
        }}>
          {isLive ? alertCount : '-'}
        </span>
      </td>
      <td style={{ padding: `${SPACE[12]}px` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: SPACE[6], flexWrap: 'wrap', minWidth: 0 }}>
          {!readOnly && (
            <Btn C={C} size="sm" variant={op.status === 'active' ? 'primary' : 'outline'} icon={<Icons.ArrowR size={SIZE.iconXs} />} onClick={() => onOpen(op)}>
              Enter
            </Btn>
          )}
          {op.status === 'scheduled' && canManage && (
            <Btn C={C} size="sm" variant="success" icon={<Icons.Play size={SIZE.iconXs} />} onClick={() => onActivate(op)}>
              Activate
            </Btn>
          )}
          {op.status === 'active' && canManage && (
            <Btn C={C} size="sm" variant="ghost" icon={<Icons.Pause size={SIZE.iconXs} />} onClick={() => onPause(op)}>
              Pause
            </Btn>
          )}
          {op.status === 'paused' && canManage && (
            <Btn C={C} size="sm" variant="success" icon={<Icons.Play size={SIZE.iconXs} />} onClick={() => onResume(op)}>
              Resume
            </Btn>
          )}
          {(op.status === 'active' || op.status === 'paused') && canManage && (
            <Btn C={C} size="sm" variant="danger" icon={<Icons.X size={SIZE.iconXs} />} onClick={() => onClose(op)}>
              Close
            </Btn>
          )}
          {readOnly && (
            <>
              <Btn C={C} size="sm" variant="ghost" icon={<Icons.Report size={SIZE.iconXs} />} onClick={() => onReport(op)}>
                View Report
              </Btn>
              <Btn C={C} size="sm" variant="ghost" icon={<Icons.Play size={SIZE.iconXs} />} onClick={() => onPlayback(op)}>
                Review Playback
              </Btn>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export function OperationsScreen() {
  const { theme, currentUser, setSelectedOperation, openOperationModal, openCloseModal, addActiveOperation } = useApp();
  const C = getCrowdC(theme);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recordActivity = useCrowdActivityAudit();
  const { isMock } = useMockMode();
  const requestedPath = searchParams.get('next');
  const operations = useOperations();
  const { venues } = useVenues();
  const { teams } = useTeams();
  const { taxonomy, operationEventTypeLabel } = useCrowdTaxonomy();
  const { alerts } = useAlertData();
  const liveZoneMetrics = useLiveZoneMetrics(null, !isMock, 2000);
  const [statusFilter, setStatusFilter] = useState<OperationStatusFilter>('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateRangeReady, setDateRangeReady] = useState(false);
  const [search, setSearch] = useState('');

  useCrowdFilterActivity({
    page: '/operations',
    filterValues: {
      status: statusFilter,
      venueId: venueFilter !== 'all' ? 'selected' : undefined,
      type: typeFilter,
      from: dateFrom ? 'applied' : undefined,
      to: dateTo ? 'applied' : undefined,
    },
    searchApplied: Boolean(search.trim()),
    enabled: dateRangeReady,
  });

  const canManage = currentUser.role === 'command_supervisor' || currentUser.role === 'system_admin';

  const venueOptions = useMemo(
    () => venues
      .filter(venue => venue.archivedAt === null)
      .map(venue => ({ id: venue.id, label: `${venue.name} - ${venue.city}` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [venues],
  );
  const venueLabelsById = useMemo(
    () => new Map(venues.map(venue => [venue.id, venue.name])),
    [venues],
  );
  const teamById = useMemo(
    () => new Map(teams
      .filter(team => team.status === 'active' && team.archivedAt === null)
      .map(team => [team.id, team])),
    [teams],
  );
  const venueLabel = (venueId: string) => venueLabelsById.get(venueId) ?? 'Unknown venue';

  const typeOptions = taxonomy?.operationEventTypes ?? [];

  useEffect(() => {
    if (operations.loading) return;
    setDateRangeReady(true);
  }, [operations.loading, operations.operations]);

  const visibleOperations = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return operations.operations
      .filter(op => statusFilter === 'all' ? true : operationMatchesTab(op, statusFilter))
      .filter(op => venueFilter === 'all' || op.venueId === venueFilter)
      .filter(op => typeFilter === 'all' || op.eventType === typeFilter)
      .filter(op => overlapsDateRange(op, dateFrom, dateTo))
      .filter(op => {
        if (!needle) return true;
        return [
          op.name,
          venueLabel(op.venueId),
          operationTeamNames(op, teamById).join(' '),
          op.eventType,
          operationEventTypeLabel(op.eventType),
        ].some(value => value.toLowerCase().includes(needle));
      })
      .sort((a, b) => {
        if (statusFilter === 'scheduled') return Date.parse(a.scheduledStart) - Date.parse(b.scheduledStart);
        // Live operations come first, then paused, planned and finished; newest first within each group.
        const rank = (status: string) => ({ active: 0, paused: 1, scheduled: 2, closed: 3 } as Record<string, number>)[status] ?? 4;
        const byStatus = rank(a.status) - rank(b.status);
        if (byStatus !== 0) return byStatus;
        return Date.parse(b.scheduledStart) - Date.parse(a.scheduledStart);
      });
  }, [dateFrom, dateTo, operationEventTypeLabel, operations.operations, search, statusFilter, teamById, typeFilter, venueFilter, venueLabelsById]);
  const operationAlertSummaries = useOperationAlertSummaries(visibleOperations, !isMock);
  const zoneState = useZones();
  const capacityByZoneId = useMemo(() => new Map(zoneState.zones.map(zone => [zone.id, zone.capacity] as const)), [zoneState.zones]);
  const loadIndexByOperationId = useMemo(() => {
    return new Map(visibleOperations.map(op => [
      op.id,
      isMock ? mockOperationCurrentLoadIndex(op) : operationCurrentLoadIndex(op, liveZoneMetrics.rows, capacityByZoneId),
    ]));
  }, [capacityByZoneId, isMock, liveZoneMetrics.rows, visibleOperations]);

  const summary = useMemo(() => {
    const live = visibleOperations.filter(op => op.status === 'active');
    const alertsByState = alertBreakdown(visibleOperations, alerts, isMock ? undefined : operationAlertSummaries.summariesByOperationId);
    const uniqueZoneCount = new Set(visibleOperations.flatMap(op => op.assignedZoneIds)).size;
    const highestZoneCoverage = highestZoneCoverageSummary(visibleOperations);
    const nextOperation = visibleOperations
      .filter(op => op.status === 'scheduled')
      .sort((a, b) => Date.parse(a.scheduledStart) - Date.parse(b.scheduledStart))[0] ?? null;
    return {
      operationCount: visibleOperations.length,
      liveCount: live.length,
      pausedCount: visibleOperations.filter(op => op.status === 'paused').length,
      scheduledCount: visibleOperations.filter(op => op.status === 'scheduled').length,
      historyCount: visibleOperations.filter(op => op.status === 'closed').length,
      activeAlerts: alertsByState.total,
      openAlerts: alertsByState.open,
      acknowledgedAlerts: alertsByState.acknowledged,
      escalatedAlerts: alertsByState.escalated,
      uniqueZoneCount,
      highestZoneCoverage,
      nextWindow: nextOperation ? fmtDateTime(nextOperation.scheduledStart) : 'No upcoming window',
    };
  }, [alerts, isMock, operationAlertSummaries.summariesByOperationId, visibleOperations]);

  function updateStatusFilter(next: OperationStatusFilter) {
    setStatusFilter(next);
  }

  const dateRangeInvalid = hasInvalidDateRange(dateFrom, dateTo);

  const selectStyle: CSSProperties = {
    background: C.surface,
    border: `1px solid ${C.border2}`,
    borderRadius: RADIUS.sm,
    padding: `${SPACE[7]}px ${SPACE[10]}px`,
    fontSize: FONT_SIZE.base,
    color: C.text,
    fontFamily: "'Albert Sans', sans-serif",
    cursor: 'pointer',
    outline: 'none',
    minHeight: 34,
    boxSizing: 'border-box',
  };

  function enterOperation(op: Operation) {
    setSelectedOperation(op);
    const targetPage = operationEntryPathForRole(currentUser.role, requestedPath);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.operationSelected,
      page: '/operations',
      operationId: op.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.operation,
      resourceId: op.id,
      metadata: { sourcePage: '/operations', targetPage },
    });
    navigate(targetPage);
  }

  async function activate(op: Operation) {
    const updated = await operations.activateOperation(op.id);
    addActiveOperation(updated);
    enterOperation(updated);
  }

  async function pause(op: Operation) {
    const updated = await operations.pauseOperation(op.id);
    addActiveOperation(updated);
  }

  async function resume(op: Operation) {
    const updated = await operations.resumeOperation(op.id);
    addActiveOperation(updated);
    enterOperation(updated);
  }

  function requestClose(op: Operation) {
    setSelectedOperation(op);
    openCloseModal();
  }

  function report(op: Operation) {
    setSelectedOperation(op);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: '/operations',
      operationId: op.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.operation,
      resourceId: op.id,
      metadata: { sourcePage: '/operations', targetPage: '/reports', templateId: 'operational_summary' },
    });
    navigate(`/reports?template=operational_summary&operationId=${encodeURIComponent(op.id)}&from=${encodeURIComponent(op.scheduledStart)}&to=${encodeURIComponent(op.scheduledEnd)}`);
  }

  function playback(op: Operation) {
    setSelectedOperation(op);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: '/operations',
      operationId: op.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.operation,
      resourceId: op.id,
      metadata: { sourcePage: '/operations', targetPage: '/movement' },
    });
    navigate(`/movement?mode=playback&operationId=${encodeURIComponent(op.id)}&from=${encodeURIComponent(op.scheduledStart)}&to=${encodeURIComponent(op.scheduledEnd)}`);
  }

  return (
    <div style={{
      flex: 1,
      overflowY: 'auto',
      padding: SPACE[20],
      fontFamily: "'Albert Sans', sans-serif",
      background: C.bg,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'start',
        gap: SPACE[16],
        marginBottom: SPACE[14],
      }}>
        <div>
          <KLabel C={C}>Crowd Operations</KLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[10], flexWrap: 'wrap' }}>
            <div style={{ fontSize: FONT_SIZE.display, fontWeight: FONT_WEIGHT.black, color: C.text }}>Operations Register</div>
          </div>
          {requestedPath && (
            <div style={{
              marginTop: SPACE[10],
              display: 'inline-flex',
              alignItems: 'center',
              gap: SPACE[8],
              color: C.amber,
              background: C.amberDim,
              border: `1px solid ${C.amber}44`,
              borderRadius: RADIUS.card,
              padding: `${SPACE[7]}px ${SPACE[10]}px`,
              fontSize: FONT_SIZE.xs,
              fontWeight: FONT_WEIGHT.semibold,
            }}>
              <Icons.Lock size={SIZE.iconXs} />
              Select an operation to continue to {nextLabel(requestedPath)}.
            </div>
          )}
        </div>
        {canManage && (
          <Btn C={C} variant="primary" icon={<Icons.Plus size={SIZE.iconSm} />} onClick={() => openOperationModal()}>
            New Operation
          </Btn>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: SPACE[12], marginBottom: SPACE[14] }}>
        <OperationKpiCard
          C={C}
          label="Operations in View"
          value={summary.operationCount}
          tone={summary.operationCount > 0 ? C.green : C.muted}
          icon={<Icons.Shield size={SIZE.iconMd} />}
          sub={`${summary.liveCount} active`}
          breakdown={[
            { label: 'Live',      value: summary.liveCount,      color: C.green },
            { label: 'Scheduled', value: summary.scheduledCount, color: C.teal },
            { label: 'Closed',    value: summary.historyCount,   color: C.ember },
          ]}
        />
        <OperationKpiCard
          C={C}
          label="Active Alerts"
          value={summary.activeAlerts}
          tone={summary.activeAlerts > 0 ? alertCountColor(C) : C.green}
          icon={<Icons.Alert size={SIZE.iconMd} />}
          sub="Open, acknowledged, or escalated in listed operations"
          breakdown={[
            { label: 'Open',      value: summary.openAlerts,      color: C.ember },
            { label: 'Acknowledged', value: summary.acknowledgedAlerts, color: C.amber },
            { label: 'Escalated', value: summary.escalatedAlerts, color: C.critical },
          ]}
        />
        <OperationKpiCard
          C={C}
          label="Zone Coverage"
          value={summary.uniqueZoneCount}
          tone={summary.uniqueZoneCount > 0 ? C.teal : C.muted}
          icon={<Icons.Zones size={SIZE.iconMd} />}
          sub={summary.highestZoneCoverage.label}
          subTitle={summary.highestZoneCoverage.title}
          sparkline
        />
        <OperationKpiCard
          C={C}
          label="Next Window"
          value={summary.nextWindow}
          tone={summary.scheduledCount > 0 ? C.teal : C.muted}
          icon={<Icons.Calendar size={SIZE.iconMd} />}
          sub={`${summary.pausedCount} paused | ${summary.scheduledCount} scheduled`}
          breakdown={[
            { label: 'Paused',    value: summary.pausedCount,    color: C.amber },
            { label: 'Scheduled', value: summary.scheduledCount, color: C.teal },
          ]}
        />
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: SPACE[8], marginBottom: SPACE[12], alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={statusFilter} onChange={event => updateStatusFilter(event.target.value as OperationStatusFilter)} style={selectStyle} aria-label="Filter operations by status">
            <option value="all">All statuses</option>
            {TABS.map(tab => (
              <option key={tab.id} value={tab.id}>{tab.label}</option>
            ))}
          </select>
          <select value={venueFilter} onChange={event => setVenueFilter(event.target.value)} style={selectStyle} aria-label="Filter operations by venue">
            <option value="all">All venues</option>
            {venueOptions.map(venue => (
              <option key={venue.id} value={venue.id}>{venue.label}</option>
            ))}
          </select>
          <select value={typeFilter} onChange={event => setTypeFilter(event.target.value)} style={selectStyle} aria-label="Filter operations by type">
            <option value="all">All types</option>
            {typeOptions.map(type => (
              <option key={type.code} value={type.code}>{type.label}</option>
            ))}
          </select>
          <div
            role="group"
            aria-label="Filter operations by date range"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: SPACE[6],
              background: C.surface,
              border: `1px solid ${dateRangeInvalid ? C.ember : C.border2}`,
              borderRadius: RADIUS.sm,
              padding: `${SPACE[4]}px ${SPACE[6]}px`,
              minHeight: 34,
              width: 450,
              maxWidth: '100%',
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            <span style={{
              color: C.muted,
              fontSize: FONT_SIZE.label,
              fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.field,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              flex: '0 0 auto',
            }}>Date range</span>
            <input
              type="datetime-local"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={event => {
                setDateRangeReady(true);
                setDateFrom(event.target.value);
              }}
              aria-label="Operation date range start"
              style={{ ...selectStyle, border: 'none', background: C.faint, minHeight: 26, padding: `${SPACE[4]}px ${SPACE[6]}px`, width: 172, flex: '0 0 172px' }}
            />
            <span style={{ color: C.muted, fontSize: FONT_SIZE.xs, whiteSpace: 'nowrap', flex: '0 0 auto' }}>to</span>
            <input
              type="datetime-local"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={event => {
                setDateRangeReady(true);
                setDateTo(event.target.value);
              }}
              aria-label="Operation date range end"
              style={{ ...selectStyle, border: 'none', background: C.faint, minHeight: 26, padding: `${SPACE[4]}px ${SPACE[6]}px`, width: 172, flex: '0 0 172px' }}
            />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: SPACE[6] }}>
            <Icons.Search size={SIZE.iconSm} />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search operations..."
              aria-label="Search operations"
              style={{ ...selectStyle, width: 200 }}
            />
          </div>
        </div>

        {operations.loading ? (
          <Panel C={C} style={{ padding: SPACE[24], color: C.muted }}>Loading operations...</Panel>
        ) : operations.error ? (
          <Panel C={C} style={{ padding: SPACE[24], color: C.ember }}>{operations.error}</Panel>
        ) : visibleOperations.length === 0 ? (
          <EmptyState C={C} statusFilter={statusFilter} />
        ) : (
          <Panel C={C} style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 1120, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 4 }} />
                {OPERATION_REGISTER_COLUMNS.map(column => (
                  <col key={column.heading} style={{ width: column.width }} />
                ))}
              </colgroup>
              <thead>
                <tr style={{ borderBottom: `1.5px solid ${C.border2}`, background: C.surface }}>
                  <th style={{ width: 4, padding: 0 }} />
                  {OPERATION_REGISTER_COLUMNS.map(column => (
                    <th key={column.heading} style={{
                      padding: `${SPACE[9]}px ${SPACE[12]}px`,
                      textAlign: column.align,
                      fontSize: FONT_SIZE.label,
                      fontWeight: FONT_WEIGHT.bold,
                      letterSpacing: LETTER_SPACING.field,
                      textTransform: 'uppercase',
                      color: C.muted,
                    }}>{column.heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleOperations.map(op => (
                  <OperationRow
                    key={`${statusFilter}-${op.id}`}
                    C={C}
                    op={op}
                    statusFilter={statusFilter}
                    venueLabel={venueLabel}
                    eventTypeLabel={operationEventTypeLabel}
                    teamById={teamById}
                    alertCount={activeAlertCount(op, alerts, isMock ? undefined : operationAlertSummaries.summariesByOperationId)}
                    loadIndex={loadIndexByOperationId.get(op.id) ?? { value: null, observedZones: 0, totalZones: op.assignedZoneIds.length, highestZoneLoad: null, stale: false }}
                    canManage={canManage}
                    onOpen={enterOperation}
                    onActivate={activate}
                    onPause={pause}
                    onResume={resume}
                    onClose={requestClose}
                    onReport={report}
                    onPlayback={playback}
                  />
                ))}
              </tbody>
            </table>
            </div>
          </Panel>
        )}
      </div>

      <div style={{ marginTop: SPACE[12], fontSize: FONT_SIZE.xs, color: C.muted, lineHeight: 1.5 }}>
        Archived is a read-only historical view over closed operations in this MVP. It does not create a separate operation status.
      </div>
    </div>
  );
}
